import React from "react";
import { BarChart3, Globe2, ShieldCheck, Smartphone } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { SurfaceCard } from "../components/designSystem";

const pillars = [
  {
    icon: BarChart3,
    title: "Performance intelligence",
    description: "MekEdge is built to turn trade history into practical insight around edge, discipline, and risk behavior.",
  },
  {
    icon: ShieldCheck,
    title: "Trust and structure",
    description: "The product should feel credible enough for serious retail traders, funded traders, mentors, and small trading desks.",
  },
  {
    icon: Smartphone,
    title: "Mobile-first execution",
    description: "Traders need to journal while the trade context is fresh, not hours later when memory has already softened the details.",
  },
  {
    icon: Globe2,
    title: "Africa-first ambition",
    description: "The long-term positioning is a trading performance operating system that starts in Nigeria and scales across Africa.",
  },
];

function About() {
  return (
    <div className="min-h-screen bg-[#f3efe7] text-slate-950 dark:bg-[linear-gradient(180deg,_#020617_0%,_#111827_45%,_#0f172a_100%)] dark:text-slate-100">
      <PageHeader 
        title="About MekEdge"
        subtitle="Helping traders journal smarter, analyze deeper, and build the operating discipline serious performance demands.We operate under MekEdge LTD. (RC No. 8976419), a fully registered Nigerian entity based in Lagos, Nigeria."
        eyebrow="About the platform"
      />
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
          <SurfaceCard className="bg-white dark:bg-gray-900">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">Mission</p>
            <h2 className="mt-4 font-serif text-4xl tracking-tight text-slate-950 dark:text-white">
              Make trade journaling feel like a real performance system, not a spreadsheet chore.
            </h2>
            <p className="mt-6 text-base leading-8 text-slate-600 dark:text-slate-300">
              MekEdge exists because too many traders still review performance through memory, screenshots in chats, or disconnected spreadsheets.
              That makes it hard to see what actually drives returns, what causes drawdowns, and whether discipline is improving over time.
            </p>
            <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-300">
              The goal is to give traders a workspace that feels serious enough for funded-account preparation, mentoring workflows, and team review, while still being fast enough for daily use.
            </p>
          </SurfaceCard>

          <SurfaceCard className="overflow-hidden bg-[linear-gradient(135deg,_#0f172a,_#111827_58%,_#1d4ed8)] text-white dark:border-gray-700">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sky-200">Positioning</p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight">A trading performance operating system for modern African traders.</h2>
            <div className="mt-8 space-y-4">
              <div className="rounded-3xl border border-white/10 bg-white/10 p-4">
                <p className="text-sm font-semibold">Who it serves</p>
                <p className="mt-2 text-sm leading-7 text-slate-200">Retail traders, funded traders, mentors, and trading teams that want more structure around execution review.</p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-white/10 p-4">
                <p className="text-sm font-semibold">Why it matters</p>
                <p className="mt-2 text-sm leading-7 text-slate-200">A better journal improves not just record keeping, but actual decision quality, accountability, and capital protection.</p>
              </div>
            </div>
          </SurfaceCard>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {pillars.map(({ icon: Icon, title, description }) => (
            <SurfaceCard key={title} className="bg-white dark:bg-gray-900">
              <div className="inline-flex rounded-2xl bg-[#f3efe7] p-3 text-slate-900 dark:bg-gray-800 dark:text-white">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 text-xl font-semibold text-slate-950 dark:text-white">{title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">{description}</p>
            </SurfaceCard>
          ))}
        </div>
      </div>
    </div>
  );
}

export default About;